import {
  REALTIME_SUBSCRIBE_STATES,
  createClient,
  SupabaseClient,
} from "@supabase/supabase-js";
import { ChatRepository } from "../repositories/chat.repository";
import { RoomRepository } from "../repositories/room.repository";
import { ProjectUserRepository } from "../repositories/project-user.repository";
import { Chat } from "../entities/chat";
import {
  CreateChatDTO,
  UpdateChatDTO,
  ChatDTO,
  ChatMapper,
} from "../dtos/chat.dto";
import { ChatWithSourcesDTO } from "../dtos/chat-with-sources.dto";
import {
  PaginationOptions,
  PaginatedResponse,
} from "../types/pagination.types";
import { invokeEdgeFunctionAsync } from "../utils/async-edge-function";

export class ChatService {
  private supabase: SupabaseClient;
  private chatRepository: ChatRepository;
  private roomRepository: RoomRepository;
  private projectUserRepository: ProjectUserRepository;

  constructor(supabaseUrl: string, supabaseAnonKey: string) {
    const supabase = createClient(supabaseUrl, supabaseAnonKey, {
      realtime: {
        heartbeatIntervalMs: 15000, // More frequent heartbeats
        reconnectAfterMs: () => [1000, 2000, 5000, 10000, 15000, 30000], // More aggressive reconnection
        timeout: 20000, // Connection timeout
      },
    });
    this.supabase = supabase;
    this.chatRepository = new ChatRepository(supabase);
    this.roomRepository = new RoomRepository(supabase);
    this.projectUserRepository = new ProjectUserRepository(supabase);
  }

  /**
   * Send a user message and trigger AI response
   */
  async sendUserMessage(dto: CreateChatDTO, userId: string): Promise<ChatDTO> {
    // Verify user has access to the room
    const room = await this.roomRepository.findById(dto.roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    if (room.user_id !== userId) {
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        room.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("User does not have access to this room");
      }
    }

    // Create user message
    const savedUserChat = await this.chatRepository.create({
      room_id: dto.roomId,
      user_id: userId,
      role: "user",
      message: dto.message,
    });

    // Call Edge Function to create assistant message and trigger AI response
    // Fire-and-forget: We don't wait for the response
    // Get Supabase URL and service key from the client
    const { supabaseUrl, supabaseKey } = this.supabase as any;

    // Get user token from Supabase auth session
    let token: string | undefined;
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      token = session?.access_token;
    } catch (error) {
      console.error("Failed to get user token:", error);
      throw new Error("Authentication required - unable to get user session");
    }

    // User token is required for security
    if (!token) {
      throw new Error("Authentication required - no valid session found");
    }

    // This function returns immediately and runs the Edge Function in background
    invokeEdgeFunctionAsync(
      supabaseUrl,
      "create-assistant-message",
      {
        roomId: dto.roomId,
        userChatId: savedUserChat.id,
        mode: dto.mode,  // Pass chat mode (chat/edit/compose)
        currentDocumentId: dto.currentDocumentId,  // Pass current document ID for compose mode
      },
      token,
    );

    // Return the user chat immediately
    return ChatMapper.toDTO(savedUserChat);
  }

  /**
   * Get chat messages for a room
   */
  async getRoomChats(roomId: string, userId: string): Promise<ChatDTO[]> {
    // Verify user has access to the room
    const room = await this.roomRepository.findById(roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    if (room.user_id !== userId) {
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        room.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("User does not have access to this room");
      }
    }

    const chats = await this.chatRepository.findByRoom(roomId);
    return chats.map((chat) => ChatMapper.toDTO(chat));
  }

  /**
   * Get chat messages for a room (paginated)
   */
  async getRoomChatsPaginated(
    roomId: string,
    userId: string,
    options: PaginationOptions,
  ): Promise<PaginatedResponse<ChatDTO>> {
    // Verify user has access to the room
    const room = await this.roomRepository.findById(roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    if (room.user_id !== userId) {
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        room.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("User does not have access to this room");
      }
    }

    const result = await this.chatRepository.findByRoomPaginated(
      roomId,
      options,
    );
    return {
      data: result.data.map((chat) => ChatMapper.toDTO(chat)),
      pagination: result.pagination,
    };
  }

  /**
   * Get latest chat messages
   */
  async getLatestChats(
    roomId: string,
    userId: string,
    limit: number = 20,
  ): Promise<ChatDTO[]> {
    // Verify user has access
    const room = await this.roomRepository.findById(roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    if (room.user_id !== userId) {
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        room.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("User does not have access to this room");
      }
    }

    const chats = await this.chatRepository.getLatestChats(roomId, limit);
    return chats.map((chat) => ChatMapper.toDTO(chat));
  }

  /**
   * Update a chat message (only user's own messages)
   */
  async updateChat(
    chatId: string,
    dto: UpdateChatDTO,
    userId: string,
  ): Promise<ChatDTO> {
    const chat = await this.chatRepository.findById(chatId);
    if (!chat) {
      throw new Error("Chat message not found");
    }

    // Only allow users to update their own messages
    if (chat.user_id !== userId || chat.role !== "user") {
      throw new Error("You can only update your own messages");
    }

    // Update message using entity method
    if (dto.message) {
      chat.updateMessage(dto.message);
    }
    if (dto.metadata) {
      chat.updateMetadata(dto.metadata);
    }

    const updatedChat = await this.chatRepository.save(chat);
    return ChatMapper.toDTO(updatedChat);
  }

  /**
   * Delete a chat message (only user's own messages)
   */
  async deleteChat(chatId: string, userId: string): Promise<void> {
    const chat = await this.chatRepository.findById(chatId);
    if (!chat) {
      throw new Error("Chat message not found");
    }

    // Only allow users to delete their own messages
    if (chat.user_id !== userId || chat.role !== "user") {
      throw new Error("You can only delete your own messages");
    }

    await this.chatRepository.delete(chatId);
  }

  /**
   * Search messages in a room
   */
  async searchChats(
    roomId: string,
    searchTerm: string,
    userId: string,
  ): Promise<ChatDTO[]> {
    // Verify user has access
    const room = await this.roomRepository.findById(roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    if (room.user_id !== userId) {
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        room.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("User does not have access to this room");
      }
    }

    const chats = await this.chatRepository.searchInRoom(roomId, searchTerm);
    return chats.map((chat) => ChatMapper.toDTO(chat));
  }

  /**
   * Get chat messages with source locations
   */
  async getRoomChatsWithSources(
    roomId: string,
    userId: string,
  ): Promise<ChatWithSourcesDTO[]> {
    // Get regular chats first
    const chats = await this.getRoomChats(roomId, userId);

    // Get room to find specification
    const room = await this.roomRepository.findById(roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    // Find the latest specification ID from assistant messages
    let currentSpecificationId: string | null = null;
    for (const chat of chats.reverse()) {
      if (chat.role === "assistant" && chat.specificationId) {
        currentSpecificationId = chat.specificationId;
        break;
      }
    }

    if (!currentSpecificationId) {
      // No specification found, return chats without sources
      return chats;
    }

    // Enhance chats with source locations
    const enhancedChats: ChatWithSourcesDTO[] = [];

    for (const chat of chats) {
      const enhancedChat: ChatWithSourcesDTO = { ...chat };

      // Only process assistant messages with source references
      if (
        chat.role === "assistant" &&
        chat.sourceReferences &&
        chat.sourceReferences.length > 0
      ) {
        // Get segment locations
        enhancedChat.sourceLocations =
          await this.segmentService.getSegmentsWithLocations(
            currentSpecificationId,
            chat.sourceReferences,
          );
      }

      enhancedChats.push(enhancedChat);
    }

    return enhancedChats;
  }

  /**
   * Get a single chat with its source highlights in structured content
   */
  async getChatWithStructuredContent(
    chatId: string,
    userId: string,
  ): Promise<ChatWithSourcesDTO | null> {
    const chat = await this.chatRepository.findById(chatId);
    if (!chat) return null;

    // Get room to verify access
    const room = await this.roomRepository.findById(chat.room_id);
    if (!room) {
      throw new Error("Room not found");
    }

    if (room.user_id !== userId) {
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        room.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("User does not have access to this chat");
      }
    }

    const chatDTO = ChatMapper.toDTO(chat);
    const enhancedChat: ChatWithSourcesDTO = { ...chatDTO };

    // If it's an assistant message with sources
    if (
      chat.role === "assistant" &&
      chat.source_references &&
      chat.source_references.length > 0
    ) {
      // Note: segment locations and structured content functionality
      // has been removed as specifications no longer exist
      enhancedChat.sourceLocations = [];
      enhancedChat.structuredContentWithHighlights = null;
    }

    return enhancedChat;
  }

  /**
   * Search messages in a room (paginated)
   */
  async searchChatsPaginated(
    roomId: string,
    searchTerm: string,
    userId: string,
    options: PaginationOptions,
  ): Promise<PaginatedResponse<ChatDTO>> {
    // Verify user has access
    const room = await this.roomRepository.findById(roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    if (room.user_id !== userId) {
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        room.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("User does not have access to this room");
      }
    }

    const result = await this.chatRepository.searchInRoomPaginated(
      roomId,
      searchTerm,
      options,
    );
    return {
      data: result.data.map((chat) => ChatMapper.toDTO(chat)),
      pagination: result.pagination,
    };
  }

  /**
   * Subscribe to real-time chat updates
   */
  subscribeToRoom(
    roomId: string,
    userId: string,
    callbacks: {
      onConnectEstablished?: (
        status: REALTIME_SUBSCRIBE_STATES,
        err?: Error,
      ) => void;
      onNewMessage?: (chat: ChatDTO) => void;
      onMessageUpdated?: (chat: ChatDTO) => void;
      onMessageDeleted?: (chatId: string) => void;
    },
  ): Promise<() => void> {
    return new Promise(async (resolve, reject) => {
      try {
        // Verify user has access
        const room = await this.roomRepository.findById(roomId);
        if (!room) {
          throw new Error("Room not found");
        }

        if (room.user_id !== userId) {
          const projectUser =
            await this.projectUserRepository.findByProjectAndUser(
              room.project_id,
              userId,
            );
          if (!projectUser) {
            throw new Error("User does not have access to this room");
          }
        }

        // Subscribe to realtime updates
        const unsubscribe = this.chatRepository.subscribeToRoom(roomId, {
          onConnect: callbacks.onConnectEstablished,
          onInsert: callbacks.onNewMessage
            ? (chat) => callbacks.onNewMessage!(ChatMapper.toDTO(chat))
            : undefined,
          onUpdate: callbacks.onMessageUpdated
            ? (chat) => callbacks.onMessageUpdated!(ChatMapper.toDTO(chat))
            : undefined,
          onDelete: callbacks.onMessageDeleted,
        });

        resolve(unsubscribe);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Unsubscribe from a room
   */
  unsubscribeFromRoom(roomId: string): void {
    this.chatRepository.unsubscribeFromRoom(roomId);
  }

  /**
   * Unsubscribe from all rooms
   */
  unsubscribeFromAllRooms(): void {
    this.chatRepository.unsubscribeAll();
  }
}
